import {afterNextRender, DestroyRef, Directive, ElementRef, inject, input} from '@angular/core';

@Directive({selector: '[scrollHeight]', standalone: true})
export class ScrollHeightDirective {
  bottomPadding = input<number>(70);
  minHeight = input<number>(200);
  // Set height rather than max-height, for layouts (e.g. grid) that need a definite container height.
  fixedHeight = input<boolean>(false);

  private host = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);
  private resizeListener: (() => void) | undefined;
  private visibilityObserver: IntersectionObserver | undefined;

  constructor() {
    afterNextRender(() => {
      this.updateHeight();
      this.resizeListener = () => this.updateHeight();
      window.addEventListener('resize', this.resizeListener);
      // A lazy tab panel is built while it is still hidden, so the first measurement can happen
      // before the element has a position on screen. Measure again once it actually appears.
      this.visibilityObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.updateHeight();
        }
      });
      this.visibilityObserver.observe(this.host.nativeElement);
    });
    this.destroyRef.onDestroy(() => {
      if (this.resizeListener) window.removeEventListener('resize', this.resizeListener);
      this.visibilityObserver?.disconnect();
    });
  }

  private updateHeight(): void {
    const el = this.host.nativeElement;
    // A hidden element measures as zero, which would cap it at almost the whole viewport - taller
    // than the room it has, so the page scrolls instead of the content. Keep the last good value.
    if (el.getClientRects().length === 0) {
      return;
    }
    const top = el.getBoundingClientRect().top;
    const height = `${Math.max(this.minHeight(), window.innerHeight - top - this.bottomPadding())}px`;
    if (this.fixedHeight()) {
      el.style.height = height;
    } else {
      el.style.maxHeight = height;
    }
  }
}
